/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component } from '@angular/core';
import { DashboardService } from '../../../../services/component';
import { UtilsService } from '../../../../../../../core/services';
import { WidgetComponent } from '../widgetBaseComponent/widget.component';
import { WidgetModel } from '../../../../models';

@Component({
    selector: 'app-namespaces-widget',
    templateUrl: '../widgetBaseComponent/widget.component.html',
    styleUrls: ['../widgetBaseComponent/widget.component.scss']
})
export class NamespaceWidgetComponent extends WidgetComponent {
    constructor(
        dashboardService: DashboardService,
        utilsService: UtilsService
    ) {
        super(dashboardService, utilsService);

        const data = {
            adcs: this.dashboardService.allData.adcs,
            namespaces: this.dashboardService.allData.namespaces,
        };

        this.setUpPage(data);
    }

    /**
     * Set up page.
     * @param data
     * @private
     */
    private setUpPage(data: { adcs: any; namespaces: any; }): void {
        this.view = null;
        const namespaceDetails: any = this.formatNamespaceData(data.adcs, data.namespaces);
        this.view = this.getNamespaceViewData(namespaceDetails);
        this.loaded = true;
    }

    /**
     * Format namespaces data.
     * @param adcList
     * @param namespaces
     * @private
     */
    private formatNamespaceData(adcList: any[] = [], namespaces: any[] = []): any {
        const namespaceDetails = {
            count: namespaces?.length || 0,
            mostActive: 'n/a',
            leastActive: 'n/a'
        };

        if (adcList?.length) {
            const namespaceIds: any[] = adcList.map(item => item.namespaceID);
            const cleanNamespacesIds: any = this.utilsService.cleanArray(namespaceIds);
            if (cleanNamespacesIds.length && namespaces.length) {
                const counts: any = this.utilsService.countArrayDuplicates(cleanNamespacesIds);
                const mostUsedId: string = this.utilsService.getCountId(counts, 'most');
                const leastUsedId: string = this.utilsService.getCountId(counts, 'least');
                namespaceDetails.mostActive = namespaces.find(item => item.id.toString() === mostUsedId).name;
                namespaceDetails.leastActive = namespaces.find(item => item.id.toString() === leastUsedId).name;
            }
        }

        return namespaceDetails;
    }

    /**
     * Get namespaces view data.
     * @param namespaceDetails
     * @private
     */
    private getNamespaceViewData(namespaceDetails: { count: any; mostActive: any; leastActive: any; }): any {
        let viewObject: WidgetModel = this.getBaseMessaging(this.baseView);

        const error: any = this.errors?.namespaces;
        if (error) {
            return this.getErrorMessaging(viewObject, error);
        }

        if (namespaceDetails?.count > 0) {
            viewObject = this.getActiveMessaging(viewObject, namespaceDetails);
        }

        return viewObject;
    }

    /**
     * Get base view messaging.
     * @param baseView
     * @private
     */
    private getBaseMessaging(baseView: WidgetModel): WidgetModel {
        const baseData = {
            iconSrc: 'assets/centralisedManagement/icons/globe.svg',
            title: 'namespaces',
            flagState: 'success',
            subtitle: 'Add a namespaces',
            message: 'You can add unlimited namespaces in your organization.',
            linkText: 'Add a namespaces',
            linkUrl: '/cm/organisation/namespaces',
        };

        return this.updateObject(baseView, baseData);
    }

    /**
     * Get active messaging.
     * @param viewObject
     * @param namespaceDetails
     * @private
     */
    private getActiveMessaging(viewObject: WidgetModel, namespaceDetails: {
        count: any;
        mostActive: any;
        leastActive: any;
    }): WidgetModel {
        const newData = {
            flagState: 'null',
            subtitle: null,
            message: `<span class="block-title">Most active:</span> ${namespaceDetails.mostActive}
                </br>
                <span class="block-title">Least active:</span> ${namespaceDetails.leastActive}`,
            linkText: 'View my Namespaces',
            linkUrl: '/cm/organisation/namespaces',
            counters: [
                {
                    count: `${namespaceDetails?.count}`,
                    countSubTitle: namespaceDetails?.count === 1 ? 'Namespace' : 'Namespaces',
                    subTitleClass: 'success'
                }
            ]
        };

        return this.updateObject(viewObject, newData);
    }
}
